import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { servicesAPI } from '../api/services';
import { 
  ArrowLeft, 
  Check, 
  Clock, 
  DollarSign, 
  Shield,
  Zap,
  Users,
  ExternalLink,
  BarChart
} from 'lucide-react';
import Loader from '../components/common/Loader';
import SeoMeta from '../components/SeoMeta';

export default function ServiceDetails() {
  const { id } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [examples, setExamples] = useState([]);

  const loadServiceDetails = useCallback(async () => {
    try {
      const serviceResponse = await servicesAPI.getById(id);
      setService(serviceResponse.data);
      
      // Données d'exemple
      setExamples([
        {
          title: "Site vitrine corporate",
          image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600",
          description: "Design moderne et responsive avec animations avancées",
          category: "Web Design",
          tags: ["Responsive", "Moderne", "Performance"]
        },
        {
          title: "Application de gestion",
          image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600",
          description: "Dashboard interactif avec analytics en temps réel",
          category: "Développement",
          tags: ["Dashboard", "Analytics", "Sécurité"]
        },
        {
          title: "Campagne marketing",
          image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600",
          description: "Stratégie complète réseaux sociaux et email marketing",
          category: "Marketing",
          tags: ["SEO", "Social Media", "Conversion"]
        }
      ]);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadServiceDetails();
  }, [loadServiceDetails]);

  if (loading) return <Loader />;
  if (!service) return <div className="text-center py-32 text-white pt-20">Service non trouvé</div>;

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    description: service.description,
    provider: {
      "@type": "Organization",
      name: "Dev-Tech",
      url: "https://devtechpro.fr",
      logo: "https://devtechpro.fr/logo-og.png"
    },
    areaServed: "FR",
    offers: service.priceStartingFrom
      ? {
          "@type": "Offer",
          priceCurrency: "EUR",
          price: service.priceStartingFrom,
          url: `https://devtechpro.fr/services/${service._id}`
        }
      : undefined
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 pt-20">
      <SeoMeta
        title={service.title}
        description={service.description}
        canonical={`https://devtechpro.fr/services/${service._id}`}
        openGraph={{
          title: service.title,
          description: service.description,
          url: `https://devtechpro.fr/services/${service._id}`,
          image: service.previewImage || "https://devtechpro.fr/logo-og.png"
        }}
        jsonLd={serviceJsonLd}
      />
      {/* Header avec bouton retour */}
      <div className="container mx-auto px-4 pt-12 pb-20">
        <div className="mb-12">
          <Link 
            to="/services" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-800/50 text-gray-300 border border-gray-700 rounded-xl hover:bg-gray-700/50 hover:text-white transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour aux services
          </Link>
        </div>

        {/* Hero Section */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6 backdrop-blur-sm">
            <span className="text-sm font-semibold text-blue-300 uppercase tracking-wide">Service Premium</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            {service.title}
          </h1>
          
          {service.priceStartingFrom && (
            <div className="flex items-center justify-center gap-3 text-2xl font-bold text-white mb-8">
              <DollarSign className="w-8 h-8 text-blue-400" />
              <span>À partir de</span>
              <span className="text-4xl text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                {service.priceStartingFrom}€
              </span>
            </div>
          )}
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {service.description}
          </p>
        </div>

        {/* Informations clés */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {service.deliveryTime && (
            <div className="group relative bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-2xl p-6 text-center transition-all duration-300 hover:border-blue-500/50 hover:transform hover:-translate-y-2">
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-400" />
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">Délai de livraison</h4>
              <p className="text-gray-400">{service.deliveryTime}</p>
            </div>
          )}
          
          <div className="group relative bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-2xl p-6 text-center transition-all duration-300 hover:border-green-500/50 hover:transform hover:-translate-y-2">
            <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-green-400" />
            </div>
            <h4 className="text-lg font-semibold text-white mb-2">Garantie</h4>
            <p className="text-gray-400">Support 12 mois inclus</p>
          </div>
          
          <div className="group relative bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-2xl p-6 text-center transition-all duration-300 hover:border-purple-500/50 hover:transform hover:-translate-y-2">
            <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
              <Zap className="w-6 h-6 text-purple-400" />
            </div>
            <h4 className="text-lg font-semibold text-white mb-2">Technologies</h4>
            <p className="text-gray-400">Stack moderne & performante</p>
          </div>
          
          <div className="group relative bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-2xl p-6 text-center transition-all duration-300 hover:border-pink-500/50 hover:transform hover:-translate-y-2">
            <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-pink-500/10 border border-pink-500/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-pink-400" />
            </div>
            <h4 className="text-lg font-semibold text-white mb-2">Équipe</h4>
            <p className="text-gray-400">Experts dédiés</p>
          </div>
        </div>

        {/* Fonctionnalités détaillées */}
        {service.detailedFeatures && service.detailedFeatures.length > 0 && (
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Fonctionnalités Avancées</h2>
              <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
              {service.detailedFeatures.map((feature, idx) => (
                <div key={idx} className="group bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-2xl p-8 transition-all duration-300 hover:border-blue-500/50 hover:bg-gray-800/50">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center">
                      <Check className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                      <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Processus */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Notre Processus</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
          </div>
          
          <div className="grid md:grid-cols-5 gap-6 max-w-6xl mx-auto">
            {[
              { title: 'Consultation', desc: 'Analyse approfondie de vos besoins', icon: '📋' },
              { title: 'Conception', desc: 'Design et architecture technique', icon: '🎨' },
              { title: 'Développement', desc: 'Implémentation et tests', icon: '⚡' },
              { title: 'Livraison', desc: 'Tests finaux et formation', icon: '🚀' },
              { title: 'Support', desc: 'Maintenance et optimisation', icon: '🛡️' }
            ].map((step, idx) => (
              <div key={idx} className="group relative text-center">
                <div className="relative">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-2xl font-bold">
                    {step.icon}
                  </div>
                  <div className="absolute top-8 left-1/2 w-full h-0.5 bg-gradient-to-r from-blue-500/50 to-purple-500/50 transform -translate-y-1/2 hidden md:block" style={{ width: 'calc(100% + 24px)', left: 'calc(50% + 32px)' }} />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                <p className="text-gray-400 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Exemples de réalisations */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Réalisations Similaires</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {examples.map((example, idx) => (
              <div key={idx} className="group relative isolate">
                {/* Card Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 group-hover:border-blue-500/50 transition-colors duration-300 -z-10" />
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl -z-10" />
                
                <div className="relative overflow-hidden rounded-2xl">
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900">
                    <img 
                      src={example.image} 
                      alt={example.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <button className="w-12 h-12 mx-auto bg-white text-gray-900 rounded-full flex items-center justify-center hover:bg-blue-500 hover:text-white transition-colors duration-200">
                          <ExternalLink className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <div className="inline-block px-3 py-1 bg-blue-500/10 text-blue-400 text-sm font-medium rounded-full mb-4 border border-blue-500/20">
                      {example.category}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-blue-300 transition-colors duration-200">
                      {example.title}
                    </h3>
                    <p className="text-gray-400 mb-4 leading-relaxed">
                      {example.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {example.tags.map((tag, tagIdx) => (
                        <span key={tagIdx} className="px-3 py-1 bg-gray-800/50 text-gray-400 text-sm rounded-lg border border-gray-700">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Glow Effect */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 blur transition-opacity duration-300 rounded-2xl -z-20" />
              </div>
            ))}
          </div>
        </div>

        {/* CTA Final */}
        <div className="relative max-w-4xl mx-auto">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-3xl blur-xl" />
          
          <div className="relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-3xl p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
              <BarChart className="w-10 h-10 text-white" />
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Prêt à commencer votre projet ?
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
              Contactez-nous pour un devis personnalisé et une consultation gratuite 
              avec l'un de nos experts.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact" 
                className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-200"
              >
                Demander un devis
              </Link>
              <Link 
                to="/portfolio" 
                className="px-8 py-4 bg-gray-800/50 text-gray-300 border border-gray-700 rounded-xl font-bold hover:bg-gray-700/50 hover:text-white transition-colors duration-200"
              >
                Voir notre portfolio
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}